import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, BookOpen, Play, Download, Calendar, Clock, Users, Search } from 'lucide-react';

const Resources = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', label: 'All Resources' },
    { id: 'case-studies', label: 'Case Studies' },
    { id: 'whitepapers', label: 'Whitepapers' },
    { id: 'documentation', label: 'Documentation' },
    { id: 'webinars', label: 'Webinars' },
    { id: 'guides', label: 'Implementation Guides' }
  ];

  const resources = [
    {
      id: 1,
      title: 'Global OEM Transforms Distribution with Agent-Driven Commerce',
      type: 'Case Study',
      category: 'case-studies',
      description: '$10B+ revenue OEM achieves 300% increase in distributor onboarding speed and $500M additional revenue through autonomous catalog management.',
      readTime: '8 min read',
      downloadCount: '2.3K',
      publishDate: '2025-01-15',
      featured: true,
      thumbnail: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Manufacturing', 'OEM', 'Distribution', 'ROI']
    },
    {
      id: 2,
      title: 'The Future of B2B Commerce: Agent-Driven Automation',
      type: 'Whitepaper',
      category: 'whitepapers',
      description: 'Comprehensive analysis of how AI agents are revolutionizing B2B commerce operations and the competitive advantages they provide.',
      readTime: '25 min read',
      downloadCount: '5.7K',
      publishDate: '2025-01-10',
      featured: true,
      thumbnail: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['AI', 'Automation', 'B2B', 'Future Trends']
    },
    {
      id: 3,
      title: 'Enterprise Implementation Guide: 15-Day Deployment',
      type: 'Implementation Guide',
      category: 'guides',
      description: 'Step-by-step guide for enterprise platform deployment, from initial setup to full production readiness in 15 days.',
      readTime: '35 min read',
      downloadCount: '3.1K',
      publishDate: '2025-01-08',
      thumbnail: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Implementation', 'Enterprise', 'Deployment']
    },
    {
      id: 4,
      title: 'API Documentation & Developer Resources',
      type: 'Documentation',
      category: 'documentation',
      description: 'Complete API reference, SDKs, and developer tools for integrating with the CredFlexi platform.',
      readTime: 'Reference',
      downloadCount: '12K',
      publishDate: '2025-01-20',
      thumbnail: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['API', 'Developer', 'Integration', 'Technical']
    },
    {
      id: 5,
      title: 'Financial Institution Automates Supply Chain Finance',
      type: 'Case Study',
      category: 'case-studies',
      description: 'Top-10 global bank processes 100,000+ applications with 95% reduction in processing time and $2B+ additional financing volume.',
      readTime: '12 min read',
      downloadCount: '1.8K',
      publishDate: '2025-01-05',
      thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Financial Services', 'Banking', 'Automation', 'ROI']
    },
    {
      id: 6,
      title: 'Multi-Party Transaction Orchestration Explained',
      type: 'Webinar',
      category: 'webinars',
      description: 'Live demonstration of how AI agents coordinate complex transactions involving multiple parties, suppliers, and financial institutions.',
      readTime: '45 min watch',
      downloadCount: '4.2K',
      publishDate: '2025-01-03',
      thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Webinar', 'Multi-Party', 'Live Demo', 'AI Agents']
    },
    {
      id: 7,
      title: 'Security & Compliance in Agent-Driven Commerce',
      type: 'Whitepaper',
      category: 'whitepapers',
      description: 'Comprehensive overview of security measures, compliance frameworks, and risk management in autonomous commerce platforms.',
      readTime: '20 min read',
      downloadCount: '2.9K',
      publishDate: '2024-12-28',
      thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Security', 'Compliance', 'Risk Management', 'Enterprise']
    },
    {
      id: 8,
      title: 'Construction Company Streamlines Project Management',
      type: 'Case Study',
      category: 'case-studies',
      description: 'Major construction firm manages $2B+ annual projects with 85% reduction in coordination time and 40% fewer project delays.',
      readTime: '10 min read',
      downloadCount: '1.5K',
      publishDate: '2024-12-25',
      thumbnail: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Construction', 'Project Management', 'Efficiency', 'ROI']
    },
    {
      id: 9,
      title: 'Advanced Integration Patterns & Best Practices',
      type: 'Implementation Guide',
      category: 'guides',
      description: 'Technical guide covering advanced integration patterns, error handling, and optimization strategies for enterprise deployments.',
      readTime: '40 min read',
      downloadCount: '2.7K',
      publishDate: '2024-12-20',
      thumbnail: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Integration', 'Best Practices', 'Technical', 'Enterprise']
    },
    {
      id: 10,
      title: 'AI Agents vs Traditional Automation: Performance Comparison',
      type: 'Webinar',
      category: 'webinars',
      description: 'Detailed performance analysis comparing AI agent-driven solutions with traditional automation platforms across key metrics.',
      readTime: '38 min watch',
      downloadCount: '3.8K',
      publishDate: '2024-12-18',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&h=300&q=80',
      tags: ['Webinar', 'Performance', 'Comparison', 'AI vs Traditional']
    }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesCategory = activeCategory === 'all' || resource.category === activeCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredResources = resources.filter(resource => resource.featured);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Case Study':
        return FileText;
      case 'Whitepaper':
        return BookOpen;
      case 'Webinar':
        return Play;
      case 'Implementation Guide':
        return BookOpen;
      case 'Documentation':
        return FileText;
      default:
        return FileText;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Enterprise Resources
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
            Comprehensive library of case studies, whitepapers, implementation guides, and technical documentation.
          </p>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    activeCategory === category.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      {activeCategory === 'all' && (
        <section className="py-16 bg-gradient-to-br from-purple-50 to-purple-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Featured Resources
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Essential resources for understanding and implementing agent-driven commerce solutions.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {featuredResources.map((resource) => {
                const TypeIcon = getTypeIcon(resource.type);
                return (
                  <div key={resource.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div className="h-48 overflow-hidden">
                      <img
                        src={resource.thumbnail}
                        alt={resource.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex items-center mb-4">
                        <div className="flex items-center space-x-2 text-purple-600">
                          <TypeIcon className="w-4 h-4" />
                          <span className="text-sm font-medium">{resource.type}</span>
                        </div>
                        <div className="ml-auto flex items-center space-x-4 text-gray-500 text-sm">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{resource.readTime}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Download className="w-4 h-4" />
                            <span>{resource.downloadCount}</span>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{resource.title}</h3>
                      <p className="text-gray-600 mb-6">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2">
                          {resource.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <Link
                          to="/contact"
                          className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700 transition-colors duration-200"
                        >
                          Download
                          <ArrowRight className="ml-1 w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* All Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {activeCategory === 'all' ? 'All Resources' : categories.find(cat => cat.id === activeCategory)?.label}
            </h2>
            <p className="text-gray-600">
              {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''} found
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource) => {
              const TypeIcon = getTypeIcon(resource.type);
              return (
                <div key={resource.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="h-40 overflow-hidden">
                    <img
                      src={resource.thumbnail}
                      alt={resource.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2 text-purple-600">
                        <TypeIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">{resource.type}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-500 text-sm">
                        <Clock className="w-3 h-3" />
                        <span>{resource.readTime}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{resource.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{resource.description}</p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3 text-gray-500 text-xs">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(resource.publishDate)}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Download className="w-3 h-3" />
                          <span>{resource.downloadCount}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {resource.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    <Link
                      to="/contact"
                      className="w-full py-2 px-4 bg-purple-600 text-white text-center rounded-lg font-medium hover:bg-purple-700 transition-colors duration-200 inline-flex items-center justify-center"
                    >
                      {resource.type === 'Webinar' ? 'Watch Now' : 'Download'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Stay Updated
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Get notified when we publish new case studies, whitepapers, and implementation guides.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
            />
            <button className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors duration-200 whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Need Custom Resources?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            Our experts can create custom implementation guides, technical documentation, and training materials for your specific use case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-purple-600 hover:bg-purple-50 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Request Custom Resources
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
            >
              Talk to Expert
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
